import { api } from './api';

export type Funcao = { id: number; nome: string; ordem: number };
export type Ministerio = { id: number; nome: string; chave: string; funcoes: Funcao[] };

export type Escala = {
  id: number;
  eventoId: number;
  eventoTitulo: string;
  eventoInicio: string | null;
  membroId: number;
  membroNome: string;
  funcao: string;
  ministerio: string | null;
  status: 'convocado' | 'confirmado' | 'indisponivel';
  justificativa: string | null;
  respondidoEm: string | null;
  observacao: string | null;
};

export type MinhaEscala = {
  id: number;
  eventoId: number;
  titulo: string;
  local: string | null;
  inicio: string | null;
  funcao: string;
  ministerio: string | null;
  status: 'convocado' | 'confirmado' | 'indisponivel';
  justificativa: string | null;
};

export type MembroLista = {
  id: number;
  nome: string;
  email: string | null;
  telefone: string | null;
  /** dia civil AAAA-MM-DD; nulo enquanto ninguem preencheu */
  nascimento: string | null;
  /** ultimo culto em que serviu; nulo = nunca serviu ainda */
  ultimaVezQueServiu: string | null;
  ministerios: { chave: string; nome: string }[];
  /** tem conta ligada, mesmo que ela nao sirva */
  temConta: boolean;
  /** conta ativa e papel que serve — os dois campos separados para a tela
   *  poder dizer o motivo certo em vez de um "nao pode" generico */
  podeEscalar: boolean;
};

/** Escala ja marcada para quem nao tem conta: a fila que o lider precisa zerar. */
export type PendenciaSemConta = { escalaId: number; membroId: number; nome: string };

export type Aniversariante = {
  id: number;
  nome: string;
  dia: number;
  mes: number;
  ehHoje: boolean;
  /** so chega para admin e editor — o ano de nascimento nao vai para o resto */
  fazendo?: number;
};

export const buscarAniversariantes = (mes?: string) =>
  api<{ mes: number; aniversariantes: Aniversariante[] }>(
    `/aniversariantes${mes ? `?mes=${mes}` : ''}`,
  );

export const salvarMembro = (
  id: number,
  mudancas: {
    nome?: string;
    email?: string | null;
    telefone?: string | null;
    nascimento?: string | null;
    ativo?: boolean;
  },
) => api(`/membros/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export type EventoProximo = {
  id: number;
  titulo: string;
  tipo: string;
  local: string | null;
  inicio: string | null;
  publico: boolean;
};

export const buscarMinisterios = () => api<{ ministerios: Ministerio[] }>('/ministerios');
export const criarFuncao = (ministerioId: number, nome: string) =>
  api<{ funcao: Funcao }>(`/ministerios/${ministerioId}/funcoes`, {
    method: 'POST',
    body: JSON.stringify({ nome }),
  });
export const removerFuncao = (id: number) => api(`/funcoes/${id}`, { method: 'DELETE' });

export const buscarMembros = (ministerio?: string) =>
  api<{ membros: MembroLista[] }>(`/membros${ministerio ? `?ministerio=${ministerio}` : ''}`);

export const criarMembro = (dados: {
  nome: string;
  telefone?: string | null;
  nascimento?: string | null;
  ministerios: string[];
  /** obrigatorios quando ha ministerio: quem serve precisa de conta */
  email?: string;
  senha?: string;
}) =>
  api<{ id: number; usuarioId: number | null }>('/membros', {
    method: 'POST',
    body: JSON.stringify(dados),
  });

/** Da conta a uma ficha que ja existe, sem criar uma segunda pessoa. */
export const criarContaDeMembro = (
  membroId: number,
  dados: { email: string; senha: string; ministerios?: string[] },
) =>
  api<{ usuarioId: number }>(`/membros/${membroId}/conta`, {
    method: 'POST',
    body: JSON.stringify(dados),
  });

export const buscarEventosProximos = () => api<{ eventos: EventoProximo[] }>('/eventos/proximos');

export const buscarEscalas = (ministerio: string, eventoId?: number) =>
  api<{ escalas: Escala[]; semConta: PendenciaSemConta[]; podeEditar: boolean }>(
    `/escalas?ministerio=${ministerio}${eventoId ? `&evento=${eventoId}` : ''}`,
  );

export const escalar = (dados: {
  eventoId: number;
  membroId: number;
  funcaoId: number;
  ministerioChave: string;
  /** so quando o lider viu o aviso de indisponibilidade e decidiu seguir */
  confirmarMesmoAssim?: boolean;
}) => api<{ id: number }>('/escalas', { method: 'POST', body: JSON.stringify(dados) });

export const desescalar = (id: number) => api(`/escalas/${id}`, { method: 'DELETE' });

export const buscarMinhasEscalas = () =>
  api<{ escalas: MinhaEscala[]; semVinculo: boolean }>('/minhas-escalas');

export const responderEscala = (id: number, status: 'confirmado' | 'indisponivel', justificativa?: string) =>
  api(`/escalas/${id}/responder`, {
    method: 'POST',
    body: JSON.stringify({ status, ...(justificativa ? { justificativa } : {}) }),
  });

/* ------------------------ indisponibilidade ------------------------- */

/** Periodo em que a pessoa avisou que nao pode servir, inclusivo nas pontas. */
export type Indisponibilidade = {
  id: number;
  membroId: number;
  membroNome: string | null;
  inicio: string;
  fim: string;
  motivo: string | null;
};

export const buscarMinhasIndisponibilidades = () =>
  api<{ periodos: Indisponibilidade[]; semVinculo: boolean }>('/minhas-indisponibilidades');

export const buscarIndisponibilidades = (ministerio: string) =>
  api<{ periodos: Indisponibilidade[] }>(`/indisponibilidades?ministerio=${ministerio}`);

export const marcarIndisponibilidade = (dados: {
  inicio: string;
  fim: string;
  motivo?: string | null;
  membroId?: number;
}) =>
  api<{ id: number; escalasNoPeriodo: number }>('/indisponibilidades', {
    method: 'POST',
    body: JSON.stringify(dados),
  });

export const apagarIndisponibilidade = (id: number) =>
  api(`/indisponibilidades/${id}`, { method: 'DELETE' });

/** O dia civil de Sao Paulo de um instante — e por ele que o periodo vale. */
export function diaSPde(instante: string | null): string {
  if (!instante) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(
    new Date(instante),
  );
}

/** Quem, dentre os periodos, esta impedido naquele dia. */
export function impedidoNoDia(
  periodos: Indisponibilidade[],
  membroId: number,
  dia: string,
): Indisponibilidade | null {
  if (!dia) return null;
  return (
    periodos.find((p) => p.membroId === membroId && p.inicio <= dia && p.fim >= dia) ?? null
  );
}

/** "ha 3 meses", "nunca serviu" — para a tela sugerir com contexto. */
export function desdeQueServiu(iso: string | null): string {
  if (!iso) return 'nunca serviu';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias <= 0) return 'serviu hoje';
  if (dias === 1) return 'serviu ontem';
  if (dias < 7) return `ha ${dias} dias`;
  if (dias < 30) return `ha ${Math.floor(dias / 7)} semana${dias < 14 ? '' : 's'}`;
  if (dias < 365) return `ha ${Math.floor(dias / 30)} ${dias < 60 ? 'mes' : 'meses'}`;
  return 'ha mais de um ano';
}

/** Quem esta ha mais tempo sem servir vem primeiro; nunca serviu vem antes. */
export function porRodizio(a: MembroLista, b: MembroLista): number {
  if (!a.ultimaVezQueServiu && !b.ultimaVezQueServiu) return a.nome.localeCompare(b.nome, 'pt-BR');
  if (!a.ultimaVezQueServiu) return -1;
  if (!b.ultimaVezQueServiu) return 1;
  return new Date(a.ultimaVezQueServiu).getTime() - new Date(b.ultimaVezQueServiu).getTime();
}

export const CORES_STATUS: Record<string, string> = {
  convocado: 'bg-slate-100 text-slate-600',
  confirmado: 'bg-tinta-900 text-white',
  indisponivel: 'bg-red-100 text-red-700',
};

export const NOME_STATUS: Record<string, string> = {
  convocado: 'aguardando',
  confirmado: 'confirmado',
  indisponivel: 'não pode',
};

export type EventoCalendario = {
  id: number;
  titulo: string;
  tipo: string;
  local: string | null;
  inicio: string | null;
  publico: boolean;
  escalados: { id: number; funcao: string; membroNome: string; status: string }[];
};

export const buscarCalendarioEscalas = (ministerio: string, mes: string) =>
  api<{ eventos: EventoCalendario[]; podeEditar: boolean }>(
    `/escalas/calendario?ministerio=${ministerio}&mes=${mes}`,
  );

/* ---------------------- administracao de pessoas ---------------------- */

export type Pessoa = {
  id: number;
  nome: string;
  email: string;
  papel: 'admin' | 'editor' | 'viewer' | 'voluntario' | 'membro';
  ativo: boolean;
  membroId: number | null;
  membroNome: string | null;
  /** vinculo + conta ativa + papel que serve. Vinculo sozinho nao basta. */
  podeSerEscalado: boolean;
  ultimoLogin: string | null;
};

export const buscarPessoas = () => api<{ pessoas: Pessoa[] }>('/pessoas');

export const criarPessoa = (dados: {
  nome: string;
  email: string;
  papel: string;
  senha: string;
  membroId?: number | null;
}) => api<{ id: number }>('/pessoas', { method: 'POST', body: JSON.stringify(dados) });

export const salvarPessoa = (
  id: number,
  mudancas: { nome?: string; papel?: string; ativo?: boolean; membroId?: number | null },
) => api(`/pessoas/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export const definirSenha = (id: number, senha: string) =>
  api(`/pessoas/${id}/senha`, { method: 'POST', body: JSON.stringify({ senha }) });

export type RegistroAuditoria = {
  id: number;
  acao: string;
  entidade: string;
  entidadeId: string | null;
  quem: string;
  ip: string | null;
  detalhes: string | null;
  quando: string | null;
};

export const buscarAuditoria = (pagina = 1, filtros: { entidade?: string; acao?: string } = {}) => {
  const q = new URLSearchParams({ pagina: String(pagina) });
  if (filtros.entidade) q.set('entidade', filtros.entidade);
  if (filtros.acao) q.set('acao', filtros.acao);
  return api<{ registros: RegistroAuditoria[]; total: number; pagina: number; porPagina: number }>(
    `/auditoria?${q.toString()}`,
  );
};

export const NOME_DO_PAPEL: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Equipe de mídia',
  viewer: 'Liderança (so leitura)',
  lider: '12 da Igreja',
  lider_adm: '12 da Igreja · ADM',
  voluntario: 'Voluntario (louvor/mídia)',
  membro: 'Membro da igreja',
};

/** Escalado que ainda nao respondeu — a cobranca que o sistema faz pelo lider. */
export type EscalaPendente = {
  id: number;
  membroId: number;
  membroNome: string;
  funcao: string;
  eventoId: number;
  eventoTitulo: string;
  eventoInicio: string | null;
  ministerio: string | null;
};

export const buscarPendentes = (dias = 14) =>
  api<{ pendentes: EscalaPendente[]; dias: number }>(`/escalas/pendentes?dias=${dias}`);
